import { useState, useEffect } from "react";
import axios from "axios";

const API_BASE =
  "https://rentdoc-a0d223e1499e.herokuapp.com/api/company-profile";

const inputClass =
  "w-full bg-gray-50 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-500 rounded-2xl py-3 px-4 text-sm text-gray-900 placeholder-gray-400";
const labelClass =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5";

export default function PluginsManagement({ setDialog }) {
  const [isBuildiumConnected, setIsBuildiumConnected] = useState(false);
  const [buildiumClientId, setBuildiumClientId] = useState("");
  const [buildiumClientSecret, setBuildiumClientSecret] = useState("");
  const [buildiumKeyName, setBuildiumKeyName] = useState("");
  const [displayClientId, setDisplayClientId] = useState("");
  const [displayKeyName, setDisplayKeyName] = useState("");

  const [webhookStatus, setWebhookStatus] = useState({
    hasSecret: false,
    lastVerified: null,
  });
  const [webhookSecretInput, setWebhookSecretInput] = useState("");
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [showWebhookInput, setShowWebhookInput] = useState(false);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookError, setWebhookError] = useState(null);
  const [useStandardMailbox, setUseStandardMailbox] = useState(false);
  const [standardFromEmail, setStandardFromEmail] = useState("");
  const [mailgunDnsRecords, setMailgunDnsRecords] = useState([]);
  const [isMailgunConnected, setIsMailgunConnected] = useState(false);
  const [mailgunDomain, setMailgunDomain] = useState("");
  const [mailgunFromEmail, setMailgunFromEmail] = useState("");
  const [mailgunFromName, setMailgunFromName] = useState("");
  const [mailgunDisplayDomain, setMailgunDisplayDomain] = useState("");
  const [mailgunVerified, setMailgunVerified] = useState(false);
  const [mailgunLoading, setMailgunLoading] = useState(false);
  const [mailgunError, setMailgunError] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStatus = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const buildiumRes = await axios.get(`${API_BASE}/buildium/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsBuildiumConnected(buildiumRes.data.isBuildiumConnected);
        setDisplayClientId(buildiumRes.data.clientId || "");
        setDisplayKeyName(buildiumRes.data.keyName || "");

        if (buildiumRes.data.isBuildiumConnected) {
          const webhookRes = await axios.get(
            `${API_BASE}/buildium/webhook-status`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          setWebhookStatus({
            hasSecret: webhookRes.data.hasWebhookSecret,
            lastVerified: webhookRes.data.lastVerified,
          });
        }
        try {
          const mgRes = await axios.get(`${API_BASE}/mailgun/status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setIsMailgunConnected(mgRes.data.connected);
          setMailgunDisplayDomain(mgRes.data.domain || "");
          setMailgunVerified(mgRes.data.domainVerified || false);
          setUseStandardMailbox(mgRes.data.useStandardMailbox || false);
          setStandardFromEmail(mgRes.data.standardFromEmail || "");
        } catch (err) {
          console.warn("Mailgun status fetch failed:", err.message);
        }
      } catch (err) {
        console.error("Failed to load status:", err);
        setError("Could not load plugin status");
      }
    };
    fetchStatus();
  }, []);

  const handleBuildiumConnect = async () => {
    if (!buildiumClientId.trim() || !buildiumClientSecret.trim()) {
      setError("Client ID and Client Secret are both required");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        clientId: buildiumClientId.trim(),
        clientSecret: buildiumClientSecret.trim(),
      };
      if (buildiumKeyName.trim()) payload.keyName = buildiumKeyName.trim();
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API_BASE}/buildium/connect`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsBuildiumConnected(true);
      setDisplayClientId(res.data.clientId);
      setDisplayKeyName(res.data.keyName || "Default Key");
      setBuildiumClientId("");
      setBuildiumClientSecret("");
      setBuildiumKeyName("");
      setDialog?.({
        type: "success",
        message: "Buildium connected successfully!",
      });
      const webhookRes = await axios.get(
        `${API_BASE}/buildium/webhook-status`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setWebhookStatus({
        hasSecret: webhookRes.data.hasWebhookSecret,
        lastVerified: webhookRes.data.lastVerified,
      });
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to connect Buildium";
      setError(msg);
      setDialog?.({ type: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleBuildiumDisconnect = async () => {
    if (
      !window.confirm("Disconnect Buildium? This stops all syncing & webhooks.")
    )
      return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE}/buildium/disconnect`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsBuildiumConnected(false);
      setDisplayClientId("");
      setDisplayKeyName("");
      setWebhookStatus({ hasSecret: false, lastVerified: null });
      setDialog?.({ type: "success", message: "Buildium disconnected" });
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to disconnect";
      setError(msg);
      setDialog?.({ type: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWebhookSecret = async () => {
    if (!webhookSecretInput.trim()) {
      setWebhookError("Please enter the webhook secret from Buildium");
      return;
    }
    setWebhookLoading(true);
    setWebhookError(null);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE}/buildium/webhook-secret`,
        { webhookSecret: webhookSecretInput.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setWebhookSecretInput("");
      setShowWebhookInput(false);
      setShowWebhookSecret(false);
      const statusRes = await axios.get(`${API_BASE}/buildium/webhook-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWebhookStatus({
        hasSecret: statusRes.data.hasWebhookSecret,
        lastVerified: statusRes.data.lastVerified,
      });
      setDialog?.({
        type: "success",
        message: "Webhook secret saved securely",
      });
    } catch (err) {
      const msg =
        err.response?.data?.message || "Failed to save webhook secret";
      setWebhookError(msg);
      setDialog?.({ type: "error", message: msg });
    } finally {
      setWebhookLoading(false);
    }
  };

  const handleMailgunConnect = async () => {
    if (!mailgunDomain.trim()) {
      setMailgunError("Sending domain is required");
      return;
    }
    setMailgunLoading(true);
    setMailgunError(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_BASE}/mailgun/connect`,
        {
          domain: mailgunDomain.trim(),
          fromEmail: mailgunFromEmail.trim() || undefined,
          fromName: mailgunFromName.trim() || undefined,
          useStandardMailbox,
          standardFromEmail: useStandardMailbox
            ? standardFromEmail.trim()
            : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setIsMailgunConnected(true);

      const txtSendingRecords = (res.data.dnsRecords || []).filter(
        (r) =>
          r.type?.toLowerCase() === "txt" ||
          r.record_type?.toLowerCase() === "txt",
      );
      const allTxtValid =
        txtSendingRecords.length > 0 &&
        txtSendingRecords.every((r) => r.valid === "valid");

      setMailgunDisplayDomain(res.data.domain);
      setMailgunVerified(allTxtValid);
      setMailgunDnsRecords(txtSendingRecords);
      setMailgunDomain("");
      setMailgunFromEmail("");
      setMailgunFromName("");
      setDialog?.({
        type: res.data.verified ? "success" : "warning",
        message: res.data.message,
      });
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to connect Mailgun";
      setMailgunError(msg);
      setDialog?.({ type: "error", message: msg });
    } finally {
      setMailgunLoading(false);
    }
  };

  const handleMailgunDisconnect = async () => {
    if (
      !window.confirm("Disconnect Mailgun? Renewal emails will stop sending.")
    )
      return;
    setMailgunLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_BASE}/mailgun/disconnect`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsMailgunConnected(false);
      setMailgunDisplayDomain("");
      setMailgunVerified(false);
      setMailgunDnsRecords([]);
      setDialog?.({ type: "success", message: "Mailgun disconnected" });
    } catch (err) {
      setDialog?.({ type: "error", message: "Failed to disconnect Mailgun" });
    } finally {
      setMailgunLoading(false);
    }
  };

  const handleUpdateMailboxPreference = async () => {
    setMailgunLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE}/mailgun/connect`,
        {
          domain: mailgunDisplayDomain,
          useStandardMailbox,
          standardFromEmail: useStandardMailbox
            ? standardFromEmail.trim()
            : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDialog?.({ type: "success", message: "Email preference saved" });
    } catch {
      setDialog?.({ type: "error", message: "Failed to save preference" });
    } finally {
      setMailgunLoading(false);
    }
  };

  const handleMailgunVerify = async () => {
    setVerifying(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_BASE}/mailgun/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Only care about TXT sending records — ignore CNAME, MX, DMARC etc.
      const txtSendingRecords = (res.data.sendingRecords || []).filter(
        (r) => r.record_type?.toLowerCase() === "txt",
      );

      const allTxtValid =
        txtSendingRecords.length > 0 &&
        txtSendingRecords.every((r) => r.valid === "valid");

      // Override what the server thinks — if our required TXT records are valid, we're good
      const verified = allTxtValid;

      setMailgunVerified(verified);
      setMailgunDnsRecords(
        txtSendingRecords.map((r) => ({
          type: r.record_type,
          name: r.name,
          value: r.value,
          valid: r.valid,
        })),
      );

      setDialog?.({
        type: verified ? "success" : "warning",
        message: verified
          ? "Domain DNS fully verified!"
          : "TXT records not fully verified yet. Check the records below.",
      });
    } catch (err) {
      setDialog?.({ type: "error", message: "Verification check failed" });
    } finally {
      setVerifying(false);
    }
  };

  // Filter helper used in render too
  const visibleDnsRecords = mailgunDnsRecords.filter(
    (r) => r.type?.toLowerCase() !== "cname",
  );

  return (
    <div className="mt-8 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          Plugins & Integrations
        </h2>
        <p className="text-gray-500 text-sm mt-0.5">
          Connect third-party services to extend functionality
        </p>
      </div>

      {/* ── Buildium Card ── */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
        <div className="flex flex-col lg:flex-row lg:gap-12 gap-8">
          {/* Left */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">Buildium</h3>
              <StatusBadge connected={isBuildiumConnected} />
            </div>
            <p className="text-sm text-gray-500 leading-relaxed">
              Connect Buildium to sync properties, residents, leases, and more.
              Enable real-time updates with webhooks.
            </p>
            {isBuildiumConnected && displayClientId && (
              <p className="mt-3 text-xs font-mono text-gray-400 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 inline-block">
                Client ID: {displayClientId}
                {displayKeyName ? ` · ${displayKeyName}` : ""}
              </p>
            )}

            {/* Webhook sub-section */}
            {isBuildiumConnected && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      Webhook Secret
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Real-time event updates from Buildium
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                      webhookStatus.hasSecret
                        ? "bg-green-50 text-green-700"
                        : "bg-amber-50 text-amber-600"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${webhookStatus.hasSecret ? "bg-green-500" : "bg-amber-400"}`}
                    />
                    {webhookStatus.hasSecret ? "Set & Encrypted" : "Not Set"}
                  </span>
                </div>

                {webhookStatus.hasSecret && webhookStatus.lastVerified && (
                  <p className="text-xs text-gray-400 mb-3">
                    Last verified:{" "}
                    {new Date(webhookStatus.lastVerified).toLocaleDateString(
                      "en-US",
                      {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      },
                    )}
                  </p>
                )}

                {!showWebhookInput ? (
                  <button
                    onClick={() => setShowWebhookInput(true)}
                    className="px-5 py-2.5 bg-white border border-gray-200 hover:border-indigo-400 hover:text-indigo-600 text-gray-700 text-sm font-semibold rounded-2xl transition"
                  >
                    {webhookStatus.hasSecret
                      ? "Update Webhook Secret"
                      : "Set Webhook Secret"}
                  </button>
                ) : (
                  <div className="space-y-3 max-w-md">
                    <div className="relative">
                      <input
                        type={showWebhookSecret ? "text" : "password"}
                        placeholder="Paste webhook secret from Buildium"
                        value={webhookSecretInput}
                        onChange={(e) => setWebhookSecretInput(e.target.value)}
                        className={`${inputClass} font-mono pr-16`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400 hover:text-indigo-600 transition"
                      >
                        {showWebhookSecret ? "Hide" : "Show"}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400">
                      Stored encrypted and never shown again.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleSaveWebhookSecret}
                        disabled={webhookLoading || !webhookSecretInput.trim()}
                        className="flex-1 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-2xl transition disabled:opacity-50"
                      >
                        {webhookLoading ? "Saving..." : "Save Secret"}
                      </button>
                      <button
                        onClick={() => {
                          setShowWebhookInput(false);
                          setWebhookSecretInput("");
                          setShowWebhookSecret(false);
                          setWebhookError(null);
                        }}
                        className="flex-1 px-5 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm font-semibold rounded-2xl hover:bg-gray-50 transition"
                      >
                        Cancel
                      </button>
                    </div>
                    {webhookError && <ErrorBanner message={webhookError} />}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right — connect/disconnect */}
          <div className="w-full lg:w-80 flex-shrink-0">
            {isBuildiumConnected ? (
              <button
                onClick={handleBuildiumDisconnect}
                disabled={loading}
                className="w-full px-6 py-3 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 text-sm font-semibold rounded-2xl transition disabled:opacity-50"
              >
                {loading ? "Disconnecting..." : "Disconnect Buildium"}
              </button>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>Client ID</label>
                  <input
                    type="text"
                    placeholder="Buildium Client ID"
                    value={buildiumClientId}
                    onChange={(e) => setBuildiumClientId(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>Client Secret</label>
                  <input
                    type="password"
                    placeholder="Buildium Client Secret"
                    value={buildiumClientSecret}
                    onChange={(e) => setBuildiumClientSecret(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    Key Name{" "}
                    <span className="normal-case font-normal text-gray-400">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Production Key"
                    value={buildiumKeyName}
                    onChange={(e) => setBuildiumKeyName(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <button
                  onClick={handleBuildiumConnect}
                  disabled={
                    loading ||
                    !buildiumClientId.trim() ||
                    !buildiumClientSecret.trim()
                  }
                  className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-2xl shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Connecting..." : "Connect to Buildium"}
                </button>
              </div>
            )}
            {error && <ErrorBanner message={error} />}
          </div>
        </div>
      </div>

      {/* ── Mailgun Card ── */}
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
        {/* Top row: info + actions side by side */}
        <div className="flex flex-col lg:flex-row lg:gap-12 gap-8">
          {/* Left */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                Automated Emails
              </h3>
              <StatusBadge connected={isMailgunConnected} />
              {isMailgunConnected && (
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                    mailgunVerified
                      ? "bg-green-50 text-green-700"
                      : "bg-amber-50 text-amber-600"
                  }`}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${mailgunVerified ? "bg-green-500" : "bg-amber-400"}`}
                  />
                  {mailgunVerified ? "DNS Verified" : "DNS Pending"}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 leading-relaxed">
              Connect your sending domain to enable automated emails — renewal
              notices, rent increase letters, and more.
            </p>
            {isMailgunConnected && mailgunDisplayDomain && (
              <p className="mt-3 text-xs font-mono text-gray-400 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 inline-block">
                Domain: {mailgunDisplayDomain}
              </p>
            )}
          </div>

          {/* Right — connect/disconnect */}
          <div className="w-full lg:w-80 flex-shrink-0">
            {isMailgunConnected ? (
              <div className="space-y-3">
                <button
                  onClick={handleMailgunVerify}
                  disabled={verifying}
                  className="w-full px-5 py-2.5 bg-white border border-gray-200 hover:border-indigo-400 hover:text-indigo-600 text-gray-700 text-sm font-semibold rounded-2xl transition disabled:opacity-50"
                >
                  {verifying ? "Checking DNS..." : "Re-check DNS"}
                </button>
                {/* Standard mailbox toggle — shown when connected */}
                <div className="pt-4 border-t border-gray-100 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        Standard mailbox
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Send from one shared address
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setUseStandardMailbox((v) => !v)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        useStandardMailbox ? "bg-indigo-600" : "bg-gray-200"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          useStandardMailbox ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  {useStandardMailbox && (
                    <div>
                      <label className={labelClass}>From address</label>
                      <div className="flex items-center bg-gray-50 border border-gray-200 rounded-2xl focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500">
                        <input
                          type="text"
                          placeholder="notices"
                          value={standardFromEmail.split("@")[0] || ""}
                          onChange={(e) => {
                            const local = e.target.value.replace(/[@\s]/g, "");
                            const domain =
                              mailgunDisplayDomain || mailgunDomain;
                            setStandardFromEmail(
                              local ? `${local}@${domain}` : "",
                            );
                          }}
                          className="flex-1 bg-transparent py-3 px-4 text-sm text-gray-900 placeholder-gray-400 outline-none min-w-0 rounded-l-2xl"
                        />
                        <span className="py-3 px-4 text-sm text-gray-400 bg-gray-100 border-l border-gray-200 rounded-r-2xl whitespace-nowrap select-none font-mono">
                          @{mailgunDisplayDomain || mailgunDomain}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Enter the local part only — the domain is fixed to your
                        connected Mailgun domain.
                      </p>
                    </div>
                  )}

                  <button
                    onClick={handleUpdateMailboxPreference}
                    disabled={mailgunLoading}
                    className="w-full px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-2xl transition disabled:opacity-50"
                  >
                    Save preference
                  </button>
                </div>
                <button
                  onClick={handleMailgunDisconnect}
                  disabled={mailgunLoading}
                  className="w-full px-6 py-3 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 text-sm font-semibold rounded-2xl transition disabled:opacity-50"
                >
                  {mailgunLoading ? "Disconnecting..." : "Disconnect Mailgun"}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>Sending Domain</label>
                  <input
                    type="text"
                    placeholder="mg.yourcompany.com"
                    value={mailgunDomain}
                    onChange={(e) => setMailgunDomain(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    From Email{" "}
                    <span className="normal-case font-normal text-gray-400">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="email"
                    placeholder="notices@mg.yourco.com"
                    value={mailgunFromEmail}
                    onChange={(e) => setMailgunFromEmail(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    From Name{" "}
                    <span className="normal-case font-normal text-gray-400">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    placeholder="Defaults to company name"
                    value={mailgunFromName}
                    onChange={(e) => setMailgunFromName(e.target.value)}
                    className={inputClass}
                  />
                </div>
                {/* ── Standard mailbox toggle ── */}
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      Use standard mailbox
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Send all renewal emails from one shared address instead of
                      the property manager's email
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setUseStandardMailbox((v) => !v)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                      useStandardMailbox ? "bg-indigo-600" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        useStandardMailbox ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                {useStandardMailbox && (
                  <div>
                    <label className={labelClass}>Standard from address</label>
                    <input
                      type="email"
                      placeholder={`notices@${mailgunDomain || mailgunDisplayDomain || "yourdomain.com"}`}
                      value={standardFromEmail}
                      onChange={(e) => setStandardFromEmail(e.target.value)}
                      className={inputClass}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Must be an address on your connected Mailgun domain.
                    </p>
                  </div>
                )}
                <button
                  onClick={handleMailgunConnect}
                  disabled={mailgunLoading || !mailgunDomain.trim()}
                  className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-2xl shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mailgunLoading ? "Connecting..." : "Connect Mailgun Domain"}
                </button>
              </div>
            )}
            {mailgunError && <ErrorBanner message={mailgunError} />}
          </div>
        </div>

        {/* DNS records — full width below both columns */}
        {isMailgunConnected &&
          !mailgunVerified &&
          visibleDnsRecords.length > 0 && (
            <div className="mt-8 pt-8 border-t border-gray-100">
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-800">
                  Required DNS Records
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Add these TXT records to your domain's DNS settings, then
                  click Re-check DNS. Changes can take up to 48 hours to
                  propagate. Click any value to copy it.
                </p>
              </div>
              <div className="overflow-x-auto rounded-2xl border border-gray-100">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50">
                    <tr>
                      {["Status", "Type", "Name", "Value"].map((h) => (
                        <th
                          key={h}
                          className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {visibleDnsRecords.map((rec, idx) => (
                      <tr
                        key={idx}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                              rec.valid === "valid"
                                ? "bg-green-50 text-green-700"
                                : "bg-amber-50 text-amber-600"
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${rec.valid === "valid" ? "bg-green-500" : "bg-amber-400"}`}
                            />
                            {rec.valid === "valid" ? "Valid" : "Pending"}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-lg">
                            {rec.type}
                          </span>
                        </td>
                        <td
                          className="px-5 py-4 font-mono text-xs text-gray-600 break-all cursor-pointer hover:text-indigo-600 transition-colors max-w-[220px]"
                          title="Click to copy"
                          onClick={() => {
                            navigator.clipboard.writeText(rec.name);
                            setDialog?.({
                              type: "success",
                              message: "Copied name to clipboard",
                            });
                          }}
                        >
                          {rec.name}
                        </td>
                        <td
                          className="px-5 py-4 font-mono text-xs text-gray-600 break-all cursor-pointer hover:text-indigo-600 transition-colors max-w-[300px]"
                          title="Click to copy"
                          onClick={() => {
                            navigator.clipboard.writeText(rec.value);
                            setDialog?.({
                              type: "success",
                              message: "Copied value to clipboard",
                            });
                          }}
                        >
                          {rec.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

function StatusBadge({ connected }) {
  return connected ? (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
      Connected
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
      Not Connected
    </span>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-sm font-medium">
      {message}
    </div>
  );
}
